package fr.manyverse.app.presentation.navigation

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavHostController
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.toRoute
import fr.manyverse.app.domain.model.CharacterMode
import fr.manyverse.app.domain.model.FeedbackType
import fr.manyverse.app.domain.model.GameTableInfo
import fr.manyverse.app.domain.model.OAuthProvider
import fr.manyverse.app.domain.model.Universe
import fr.manyverse.app.domain.model.User
import fr.manyverse.app.presentation.components.DisableBackNavigation
import fr.manyverse.app.presentation.components.ErrorState
import fr.manyverse.app.presentation.screens.CharacterEditScreen
import fr.manyverse.app.presentation.screens.CharacterListScreen
import fr.manyverse.app.presentation.screens.CreateCharacterScreen
import fr.manyverse.app.presentation.screens.FeedbackScreen
import fr.manyverse.app.presentation.screens.TableConfigurationScreen
import fr.manyverse.app.presentation.screens.GameMasterScreen
import fr.manyverse.app.presentation.screens.GmInventoryScreen
import fr.manyverse.app.presentation.screens.OnboardingScreen
import fr.manyverse.app.presentation.screens.PlayerScreen
import fr.manyverse.app.presentation.screens.PreMadeCharactersScreen
import fr.manyverse.app.presentation.screens.UniverseEditScreen
import fr.manyverse.app.presentation.screens.UniversesListScreen
import fr.manyverse.app.presentation.screens.WelcomeScreen
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.theme.UniverseTheme
import fr.manyverse.app.presentation.theme.isSystemInDarkTheme
import fr.manyverse.app.presentation.viewmodel.ActiveGameData
import fr.manyverse.app.presentation.viewmodel.ActiveGameViewModel
import fr.manyverse.app.presentation.viewmodel.AuthUiState
import fr.manyverse.app.presentation.viewmodel.AuthViewModel
import fr.manyverse.app.presentation.viewmodel.CharacterViewModel
import fr.manyverse.app.presentation.viewmodel.GameConfigurationViewModel
import fr.manyverse.app.presentation.viewmodel.TableJoiningViewModel
import fr.manyverse.app.presentation.viewmodel.ThemeViewModel
import fr.manyverse.app.presentation.viewmodel.UserTablesViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.error_opening_table
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

private const val FEEDBACK_SENT_KEY = "feedback_sent"

@Composable
fun AuthenticatedNavigationGraph(
    navController: NavHostController,
    user: User,
    themeViewModel: ThemeViewModel,
    authViewModel: AuthViewModel,
    isNewLogin: Boolean,
    onResetNewLoginFlag: () -> Unit
) {
    LaunchedEffect(isNewLogin) {
        if (isNewLogin) {
            navController.navigate(WelcomeRoute) {
                popUpTo(0) { inclusive = true }
            }
            onResetNewLoginFlag()
        }
    }

    NavHost(
        navController = navController,
        startDestination = WelcomeRoute
    ) {
        composable<WelcomeRoute> {
            DisableBackNavigation()
            WelcomeRouteContent(
                navController = navController,
                user = user,
                themeViewModel = themeViewModel,
                authViewModel = authViewModel
            )
        }

        composable<CharacterListRoute> {
            CharacterListScreen(
                userId = user.id,
                onNavigateBack = { navController.navigateUp() },
                onCreateCharacter = { navController.navigate(CreateCharacterRoute) },
                onEditCharacter = { characterId ->
                    navController.navigate(EditCharacterRoute(characterId))
                }
            )
        }

        composable<CreateCharacterRoute> {
            CreateCharacterScreen(
                userId = user.id,
                onNavigateBack = { navController.navigateUp() }
            )
        }

        composable<EditCharacterRoute> { backStackEntry ->
            val route = backStackEntry.toRoute<EditCharacterRoute>()
            CharacterEditScreen(
                userId = user.id,
                characterId = route.characterId,
                onNavigateBack = { navController.navigateUp() }
            )
        }

        composable<CreateGameRoute> {
            LaunchedEffect(Unit) {
                navController.navigate(GameConfigurationRoute) {
                    popUpTo<WelcomeRoute>()
                }
            }
        }

        composable<GameConfigurationRoute> {
            GameConfigurationRouteContent(
                navController = navController,
                userId = user.id
            )
        }

        composable<ActiveGameRoute> { backStackEntry ->
            val route = backStackEntry.toRoute<ActiveGameRoute>()
            ActiveGameRouteContent(
                navController = navController,
                tableId = route.tableId,
                universeId = route.universeId,
                userId = user.id,
                themeViewModel = themeViewModel
            )
        }

        composable<PreMadeCharactersRoute> { backStackEntry ->
            val route = backStackEntry.toRoute<PreMadeCharactersRoute>()
            PreMadeCharactersScreen(
                tableId = route.tableId,
                themeId = route.themeId,
                themeSeedColor = route.themeSeedColor,
                isFreeConstruction = route.isFreeConstruction,
                isCharacterSheetAvailable = route.isCharacterSheetAvailable,
                onNavigateBack = { navController.navigateUp() }
            )
        }

        composable<GmInventoryRoute> { backStackEntry ->
            val route = backStackEntry.toRoute<GmInventoryRoute>()
            GmInventoryScreen(
                tableId = route.tableId,
                gmUserId = user.id,
                themeId = route.themeId,
                themeSeedColor = route.themeSeedColor,
                onNavigateBack = { navController.navigateUp() }
            )
        }

        composable<UniversesListRoute> {
            UniversesListScreen(
                userId = user.id,
                onNavigateBack = { navController.navigateUp() },
                onCreateUniverse = { navController.navigate(CreateUniverseRoute) },
                onEditUniverse = { id -> navController.navigate(EditUniverseRoute(id)) }
            )
        }

        composable<CreateUniverseRoute> {
            UniverseEditScreen(
                userId = user.id,
                editingId = null,
                onNavigateBack = { navController.navigateUp() }
            )
        }

        composable<EditUniverseRoute> { backStackEntry ->
            val route = backStackEntry.toRoute<EditUniverseRoute>()
            UniverseEditScreen(
                userId = user.id,
                editingId = route.universeId,
                onNavigateBack = { navController.navigateUp() }
            )
        }

        composable<FeedbackRoute> { backStackEntry ->
            val route = backStackEntry.toRoute<FeedbackRoute>()
            FeedbackScreen(
                type = FeedbackType.valueOf(route.type),
                userId = user.id,
                userEmail = user.email,
                onNavigateBack = { navController.navigateUp() },
                onSubmitted = {
                    navController.previousBackStackEntry?.savedStateHandle?.set(FEEDBACK_SENT_KEY, true)
                    navController.navigateUp()
                }
            )
        }
    }
}

@Composable
private fun WelcomeRouteContent(
    navController: NavHostController,
    user: User,
    themeViewModel: ThemeViewModel,
    authViewModel: AuthViewModel
) {
    val characterViewModel = koinViewModel<CharacterViewModel>()
    val tableJoiningViewModel = koinViewModel<TableJoiningViewModel>()
    val userTablesViewModel = koinViewModel<UserTablesViewModel>()
    val authUiState by authViewModel.uiState.collectAsStateWithLifecycle()
    val characterCountState by characterViewModel.characterCountState.collectAsStateWithLifecycle()
    val joinTableState by tableJoiningViewModel.joinTableState.collectAsStateWithLifecycle()
    val userActiveTablesState by userTablesViewModel.userActiveTablesState.collectAsStateWithLifecycle()

    var joinError by remember { mutableStateOf<String?>(null) }

    val welcomeBackStackEntry = remember(navController) { navController.getBackStackEntry(WelcomeRoute) }
    val showFeedbackThankYou by welcomeBackStackEntry.savedStateHandle
        .getStateFlow(FEEDBACK_SENT_KEY, false)
        .collectAsStateWithLifecycle()

    val characterCount = when (val state = characterCountState) {
        is AsyncUiState.Success -> state.data
        else -> 0
    }

    val activeTables = when (val state = userActiveTablesState) {
        is AsyncUiState.Success ->
            state.data.map { tableInfo ->
                GameTableInfo(
                    table = tableInfo.table,
                    role = tableInfo.role
                )
            }

        else -> emptyList()
    }

    val isLoadingTables = userActiveTablesState is AsyncUiState.Loading
    // Sans ce message, un échec de chargement passerait pour « aucune table active »
    val tablesError = (userActiveTablesState as? AsyncUiState.Error)?.message

    LaunchedEffect(user.id) {
        characterViewModel.loadCharacterCount(user.id)
        userTablesViewModel.loadAllUserActiveTables(user.id)
    }

    // Écoute limitée à l'affichage de l'accueil : quitter l'écran coupe l'abonnement
    LaunchedEffect(user.id) {
        userTablesViewModel.watchMembershipChanges(user.id)
    }

    LaunchedEffect(joinTableState) {
        when (val state = joinTableState) {
            is AsyncUiState.Success -> {
                navController.navigate(ActiveGameRoute(state.data.id, state.data.universeId))
                tableJoiningViewModel.resetJoinTableState()
            }

            is AsyncUiState.Error -> {
                joinError = state.message
            }

            else -> {}
        }
    }

    WelcomeScreen(
        user = user,
        onSignOut = {
            themeViewModel.clearUniverse()
            authViewModel.signOut()
        },
        onDeleteAccount = {
            themeViewModel.clearUniverse()
            authViewModel.deleteAccount(user.id)
        },
        onNavigateToCharacters = { navController.navigate(CharacterListRoute) },
        onCreateCharacter = { navController.navigate(CreateCharacterRoute) },
        onNavigateToUniverses = { navController.navigate(UniversesListRoute) },
        onCreateGame = { navController.navigate(CreateGameRoute) },
        onJoinGame = { code ->
            joinError = null
            tableJoiningViewModel.joinTable(code, user.id)
        },
        joinError = joinError,
        isJoining = joinTableState is AsyncUiState.Loading,
        isLoading = authUiState.isLoading,
        isLoadingTables = isLoadingTables,
        tablesError = tablesError,
        onRetryTables = { userTablesViewModel.loadAllUserActiveTables(user.id) },
        characterCount = characterCount,
        activeTables = activeTables,
        onTableClick = { tableId, universeId -> navController.navigate(ActiveGameRoute(tableId, universeId)) },
        onReportBug = { navController.navigate(FeedbackRoute(FeedbackType.BUG.name)) },
        onMakeSuggestion = { navController.navigate(FeedbackRoute(FeedbackType.SUGGESTION.name)) },
        showFeedbackThankYou = showFeedbackThankYou,
        onFeedbackThankYouShown = { welcomeBackStackEntry.savedStateHandle[FEEDBACK_SENT_KEY] = false }
    )

}

@Composable
private fun GameConfigurationRouteContent(
    navController: NavHostController,
    userId: String
) {
    val gameConfigViewModel = koinViewModel<GameConfigurationViewModel>()
    val configState by gameConfigViewModel.configState.collectAsStateWithLifecycle()
    val gameName by gameConfigViewModel.gameName.collectAsStateWithLifecycle()
    val universesState by gameConfigViewModel.universesState.collectAsStateWithLifecycle()
    val selectedUniverseId by gameConfigViewModel.selectedUniverseId.collectAsStateWithLifecycle()

    val currentBackStackEntry by navController.currentBackStackEntryAsState()
    LaunchedEffect(currentBackStackEntry) {
        val route = currentBackStackEntry?.destination?.route ?: return@LaunchedEffect
        if (route.contains("GameConfigurationRoute")) {
            gameConfigViewModel.loadUniverses(userId)
        }
    }

    LaunchedEffect(configState) {
        when (val state = configState) {
            is AsyncUiState.Success -> {
                navController.navigate(ActiveGameRoute(state.data.id, state.data.universeId)) {
                    popUpTo<WelcomeRoute>()
                }
                gameConfigViewModel.resetState()
            }

            else -> {}
        }
    }

    when (configState) {
        is AsyncUiState.Idle, is AsyncUiState.Error -> {
            val universes = when (val state = universesState) {
                is AsyncUiState.Success -> state.data
                else -> emptyList()
            }
            TableConfigurationScreen(
                gameName = gameName,
                onGameNameChange = gameConfigViewModel::updateGameName,
                universes = universes,
                selectedUniverseId = selectedUniverseId,
                onUniverseSelected = gameConfigViewModel::selectUniverse,
                onCreateUniverse = { navController.navigate(CreateUniverseRoute) },
                onCancel = { navController.navigateUp() },
                onLaunch = { gameConfigViewModel.createGameTable(userId) },
                isLaunchEnabled = selectedUniverseId != null && gameName.isNotBlank(),
                errorMessage = (configState as? AsyncUiState.Error)?.message
            )
        }

        is AsyncUiState.Loading -> {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator()
            }
        }

        else -> {
            Box(Modifier.fillMaxSize())
        }
    }
}

@Composable
private fun ActiveGameRouteContent(
    navController: NavHostController,
    tableId: String,
    universeId: String,
    userId: String,
    themeViewModel: ThemeViewModel
) {
    val activeGameViewModel = koinViewModel<ActiveGameViewModel>()
    val activeGameState by activeGameViewModel.activeGameState.collectAsStateWithLifecycle()
    val gameActionState by activeGameViewModel.actionState.collectAsStateWithLifecycle()

    DisposableEffect(Unit) {
        onDispose { themeViewModel.clearUniverse() }
    }

    LaunchedEffect(activeGameState) {
        (activeGameState as? AsyncUiState.Success)?.data?.let { data ->
            themeViewModel.selectUniverse(
                Universe(
                    id = universeId,
                    displayName = data.table.universeConfig.displayName,
                    config = data.table.resolvedConfig
                )
            )
        }
    }

    LaunchedEffect(tableId, universeId) {
        activeGameViewModel.loadAndObserveActiveGame(tableId, userId)
    }

    LaunchedEffect(gameActionState) {
        if (gameActionState is AsyncUiState.Success) {
            activeGameViewModel.resetActionState()
            activeGameViewModel.stopObservingActiveGame()
            navController.navigateUp()
        }
    }

    when (val state = activeGameState) {
        is AsyncUiState.Success<ActiveGameData> -> {
            DisableBackNavigation()
            val themePreference by themeViewModel.isDarkTheme.collectAsStateWithLifecycle()
            val isDark = themePreference ?: isSystemInDarkTheme()
            UniverseTheme(config = state.data.table.resolvedConfig, isDark = isDark) {
                if (state.data.isGameMaster) {
                    GameMasterScreen(
                        table = state.data.table,
                        characters = state.data.characters,
                        tableMembers = state.data.tableMembers,
                        universeId = universeId,
                        currentUserId = userId,
                        onNavigateBack = { navController.navigateUp() },
                        onNavigateToPreMades = { tid ->
                            val config = state.data.table.resolvedConfig
                            navController.navigate(
                                PreMadeCharactersRoute(
                                    tableId = tid,
                                    themeId = config.themeId,
                                    themeSeedColor = config.themeSeedColor,
                                    isFreeConstruction = config.characterMode == CharacterMode.FREE_CONSTRUCTION,
                                    isCharacterSheetAvailable = config.features.characterSheet
                                )
                            )
                        },
                        onNavigateToGmInventory = { tid ->
                            val config = state.data.table.resolvedConfig
                            navController.navigate(
                                GmInventoryRoute(
                                    tableId = tid,
                                    themeId = config.themeId,
                                    themeSeedColor = config.themeSeedColor
                                )
                            )
                        }
                    )
                } else {
                    PlayerScreen(
                        table = state.data.table,
                        tableMembers = state.data.tableMembers,
                        characters = state.data.characters,
                        universeId = universeId,
                        currentUserId = userId,
                        onNavigateBack = { navController.navigateUp() }
                    )
                }
            }
        }

        is AsyncUiState.Idle, is AsyncUiState.Loading -> {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator()
            }
        }

        is AsyncUiState.Error -> {
            // Plein écran sans barre du haut : le bouton retour est la seule sortie sur iOS, web
            // et desktop
            ErrorState(
                message = state.message,
                title = stringResource(Res.string.error_opening_table),
                onRetry = { activeGameViewModel.loadAndObserveActiveGame(tableId, userId) },
                onBack = { navController.navigateUp() },
                modifier = Modifier.safeDrawingPadding()
            )
        }
    }
}

@Composable
fun UnauthenticatedNavigationGraph(
    navController: NavHostController,
    uiState: AuthUiState,
    authViewModel: AuthViewModel,
    isDarkTheme: Boolean,
    onToggleTheme: () -> Unit,
    deepLinkPath: String?
) {
    LaunchedEffect(deepLinkPath) {
        if (deepLinkPath == "oauth-callback") {
            authViewModel.onOAuthCallbackReceived()
        }
    }

    NavHost(
        navController = navController,
        startDestination = OnboardingRoute
    ) {
        composable<OnboardingRoute> {
            OnboardingScreen(
                isDarkTheme = isDarkTheme,
                onToggleTheme = onToggleTheme,
                uiState = uiState,
                onEmailChange = { authViewModel.onEmailChange(it) },
                onSendOtp = { authViewModel.sendOtp() },
                onOtpCodeChange = { authViewModel.onOtpCodeChange(it) },
                onVerifyOtp = { authViewModel.verifyOtp() },
                onResendOtp = { authViewModel.sendOtp() },
                onResetAuthState = { authViewModel.resetState() },
                onSignInWithGoogle = { authViewModel.signInWithOAuth(OAuthProvider.GOOGLE) },
                onSignInWithApple = { authViewModel.signInWithOAuth(OAuthProvider.APPLE) },
                onSignInAnonymously = { authViewModel.signInAnonymously() },
                onErrorShown = { authViewModel.clearError() }
            )
        }

    }
}
